/**
 * Etiketten-Vorlage `design` v1 (Story 27.3, gemeinsamer Vertrag mit Story 27.2): Zod-Schema und Typen.
 *
 * Browser-sicher: nur Zod, keine Datenbank, keine Uhr, kein Netz. Maße in mm (Zehntelmillimeter genau), Schriftgrößen
 * in pt. Texte und Strichcode-Inhalte bestehen aus Segmenten (fester Text, Feld, Datum, Uhrzeit, Zähler) – kein
 * Platzhalter-Parser. Werte, Datum, Uhrzeit, Zähler, Bilder, Schriften und Drucker sind Eingaben des Kerns (`input.ts`).
 */
import { z } from 'zod';

/** Version des Render-Kerns; steht an jeder Zeichenliste und später am Druckprotokoll (AD-42). */
export const LABEL_RENDER_VERSION = 'label-render-v1';
export type LabelRenderVersion = typeof LABEL_RENDER_VERSION;

const mm = z.number().finite().min(0).max(1000);

export const labelSegmentSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text'), text: z.string().max(2000) }),
  z.object({ kind: z.literal('field'), key: z.string().min(1).max(200) }),
  z.object({ kind: z.literal('date'), format: z.enum(['dd.MM.yyyy', 'yyyy-MM-dd', 'week', 'MM/yyyy', 'yyMMdd']) }),
  z.object({ kind: z.literal('time') }),
  z.object({ kind: z.literal('counter'), format: z.enum(['n', 'n_of_m']) }),
]);
export type LabelSegment = z.infer<typeof labelSegmentSchema>;

/** Schriften im Paket (OFL): Inter, Inter schmal (Inter Tight), Mono (JetBrains Mono). */
export const labelFontFamilies = ['inter', 'inter_tight', 'mono'] as const;
export type LabelFontFamily = (typeof labelFontFamilies)[number];

/**
 * Strichcode-Formate (Story 27.3: Code 128, EAN-13; Story 27.4: alle aus change §3.3). Reihenfolge = Anzeige im Editor
 * (mit 27.5 abgestimmt). `yyMMdd` im Datum-Segment dient GS1-Datumsfeldern wie (11).
 */
export const labelBarcodeFormats = ['code128', 'ean13', 'ean8', 'upca', 'gs1_128', 'itf14', 'code39', 'qrcode', 'datamatrix', 'gs1_datamatrix'] as const;
export type LabelBarcodeFormat = (typeof labelBarcodeFormats)[number];

/** Art des Codes: Strichcode (1D, Klartext möglich) oder Matrix-Code (2D, quadratische Module, ohne Klartext). */
export type LabelBarcodeKind = 'linear' | 'matrix';
export const labelBarcodeKind: Readonly<Record<LabelBarcodeFormat, LabelBarcodeKind>> = {
  code128: 'linear',
  ean13: 'linear',
  ean8: 'linear',
  upca: 'linear',
  gs1_128: 'linear',
  itf14: 'linear',
  code39: 'linear',
  qrcode: 'matrix',
  datamatrix: 'matrix',
  gs1_datamatrix: 'matrix',
};

/** Fehlerkorrektur des QR-Codes (ISO/IEC 18004): L ≈ 7 %, M ≈ 15 %, Q ≈ 25 %, H ≈ 30 % wiederherstellbar. */
export const labelQrEccLevels = ['L', 'M', 'Q', 'H'] as const;
export type LabelQrEcc = (typeof labelQrEccLevels)[number];

const baseShape = {
  id: z.string().min(1).max(40),
  name: z.string().max(80).optional(),
  x: mm,
  y: mm,
  w: mm,
  h: mm,
  rotation: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]).default(0),
  hideIfEmpty: z.boolean().default(false),
  locked: z.boolean().optional(),
};

export const labelTextElementSchema = z.object({
  ...baseShape,
  type: z.literal('text'),
  segments: z.array(labelSegmentSchema).max(50),
  font: z.enum(labelFontFamilies).default('inter'),
  sizePt: z.number().min(2).max(200),
  bold: z.boolean().default(false),
  align: z.enum(['left', 'center', 'right']).default('left'),
  vAlign: z.enum(['top', 'middle', 'bottom']).default('top'),
  maxLines: z.number().int().min(1).max(50).default(1),
  /** „An Fläche anpassen“: kleinste Schriftgröße in pt; `null` = feste Größe. */
  fitMinPt: z.number().min(2).max(200).nullable().default(null),
  uppercase: z.boolean().default(false),
  /** Weiß auf schwarz: der Kasten wird schwarz, der Text weiß. */
  invert: z.boolean().default(false),
});

export const labelBarcodeElementSchema = z.object({
  ...baseShape,
  type: z.literal('barcode'),
  format: z.enum(labelBarcodeFormats),
  content: z.array(labelSegmentSchema).max(50),
  /** Klartext unter dem Code (nur Strichcodes; Matrix-Codes ignorieren ihn). */
  showText: z.boolean().default(true),
  textSizePt: z.number().min(2).max(72).default(8),
  /** Modulbreite in mm; `null` = automatisch größtmöglich. Wird auf ganze Druckpunkte gerundet. */
  moduleMm: z.number().min(0.05).max(5).nullable().default(null),
  /** Fehlerkorrektur, nur beim QR-Code wirksam. */
  qrEcc: z.enum(labelQrEccLevels).default('M'),
});

export const labelLineElementSchema = z.object({ ...baseShape, type: z.literal('line'), strokeMm: z.number().min(0.05).max(50) });

export const labelRectElementSchema = z.object({
  ...baseShape,
  type: z.literal('rect'),
  strokeMm: z.number().min(0).max(50).default(0.3),
  fill: z.enum(['none', 'black']).default('none'),
  radiusMm: z.number().min(0).max(500).default(0),
});

export const labelEllipseElementSchema = z.object({
  ...baseShape,
  type: z.literal('ellipse'),
  strokeMm: z.number().min(0).max(50).default(0.3),
  fill: z.enum(['none', 'black']).default('none'),
});

export const labelImageElementSchema = z.object({
  ...baseShape,
  type: z.literal('image'),
  imageId: z.uuid(),
  fit: z.enum(['contain', 'stretch']).default('contain'),
});

export const labelElementSchema = z.discriminatedUnion('type', [
  labelTextElementSchema,
  labelBarcodeElementSchema,
  labelLineElementSchema,
  labelRectElementSchema,
  labelEllipseElementSchema,
  labelImageElementSchema,
]);
export type LabelElement = z.infer<typeof labelElementSchema>;
export type LabelTextElement = z.infer<typeof labelTextElementSchema>;
export type LabelBarcodeElement = z.infer<typeof labelBarcodeElementSchema>;
export type LabelLineElement = z.infer<typeof labelLineElementSchema>;
export type LabelRectElement = z.infer<typeof labelRectElementSchema>;
export type LabelEllipseElement = z.infer<typeof labelEllipseElementSchema>;
export type LabelImageElement = z.infer<typeof labelImageElementSchema>;

export const labelDesignSchema = z.object({
  schema: z.literal(1),
  widthMm: z.number().min(5).max(1000),
  heightMm: z.number().min(5).max(1000),
  elements: z.array(labelElementSchema).max(500),
});
/** Vorlage nach dem Prüfen (Standardwerte gesetzt). */
export type LabelDesign = z.infer<typeof labelDesignSchema>;
/** Vorlage, wie sie gespeichert/gesendet wird (Standardwerte dürfen fehlen). */
export type LabelDesignInput = z.input<typeof labelDesignSchema>;

/** A4-Bogen: Raster der Felder (Zeilen von oben, Spalten von links). */
export const labelSheetLayoutSchema = z.object({
  pageWidthMm: z.number().min(10).max(1000),
  pageHeightMm: z.number().min(10).max(1000),
  columns: z.number().int().min(1).max(50),
  rows: z.number().int().min(1).max(100),
  marginTopMm: mm,
  marginLeftMm: mm,
  gapXMm: mm,
  gapYMm: mm,
});
export type LabelSheetLayout = z.infer<typeof labelSheetLayoutSchema>;
